import { useRef, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/common/modal";
import { Button } from "components/common/buttons/Button";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { useProjectAuth } from "hooks/project/useProjectAuth";
import { useBudgetMutations } from "hooks/useBudget";
import { Permissions } from "utils/permissions/permissionConstants";
import { toastMsg } from "config";
import { handleToast, getApiErrorToastMessage } from "utils";
import { isAxiosError } from "axios";

interface BudgetImportModalProps {
  open: boolean;
  onClose: () => void;
}

const ACCEPTED_EXTENSIONS = [".xml", ".json"];
const BINARY_EXTENSIONS = [".mmbx", ".mbd"];

function getFileExtension(filename: string): string {
  const dotIndex = filename.lastIndexOf(".");
  if (dotIndex < 0) return "";
  return filename.slice(dotIndex).toLowerCase();
}

export const BudgetImportModal = ({ open, onClose }: BudgetImportModalProps) => {
  const { projectId, documentId } = useProjectIdentifiers();
  const { canProject } = useProjectAuth(projectId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [importSummary, setImportSummary] = useState<string | null>(null);

  const { importBudgetFile } = useBudgetMutations({
    projectId,
    documentId,
  });

  const resetState = () => {
    setSelectedFileName(null);
    setWarnings([]);
    setImportSummary(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFileName(file.name);
    setWarnings([]);
    setImportSummary(null);

    const extension = getFileExtension(file.name);
    if (BINARY_EXTENSIONS.includes(extension)) {
      handleToast(
        "Binary Movie Magic budget files (.mmbx/.mbd) cannot be imported. Export XML or JSON from Movie Magic Budgeting first.",
        "error"
      );
      resetState();
      return;
    }

    if (extension && !ACCEPTED_EXTENSIONS.includes(extension)) {
      handleToast("Choose a Movie Magic or RivetAI budget export (.xml or .json).", "error");
      resetState();
      return;
    }

    if (!canProject(Permissions.PROJECT_UPDATE)) {
      handleToast("You do not have permission to import a budget.", "error");
      resetState();
      return;
    }

    try {
      const contents = await file.text();
      if (contents.includes("\0")) {
        handleToast(
          "Binary Movie Magic budget files cannot be imported. Export XML or JSON from Movie Magic Budgeting first.",
          "error"
        );
        resetState();
        return;
      }

      const result = await importBudgetFile.mutateAsync({
        contents,
        filename: file.name,
      });

      const categoryCount = result.budget?.categories?.length ?? 0;
      const accountCount =
        result.budget?.categories?.reduce(
          (total, category) => total + (category.sub_categories?.length ?? 0),
          0
        ) ?? 0;

      setImportSummary(
        `Imported ${categoryCount} ${categoryCount === 1 ? "section" : "sections"} and ${accountCount} ${accountCount === 1 ? "account" : "accounts"} as a new budget version.`
      );
      setWarnings(result.warnings ?? []);
      handleToast("Budget imported successfully.", "success");
    } catch (error) {
      // 4xx: the server's reason. 5xx: generic text, never the server's raw text.
      handleToast(
        isAxiosError(error)
          ? getApiErrorToastMessage(error, toastMsg.genericServerErr)
          : "Could not import this budget file.",
        "error"
      );
      resetState();
    }
  };

  return (
    <Modal isOpen={open} onClose={handleClose}>
      <ModalHeader title="Import budget" onClose={handleClose} />
      <ModalBody>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-foreground/80">
            Import a Movie Magic Budget export (XML or JSON) or a RivetAI Budget XML backup. RivetAI
            creates a new budget version so your current version stays intact.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xml,.json,application/xml,application/json,text/xml,application/json"
            className="hidden"
            onChange={handleFileChange}
          />
          <Button variant="subtle" onClick={handleChooseFile} disabled={importBudgetFile.isPending}>
            {importBudgetFile.isPending ? "Importing…" : "Choose file"}
          </Button>
          {selectedFileName ? (
            <p className="text-sm text-muted-foreground">Selected: {selectedFileName}</p>
          ) : null}
          {importSummary ? (
            <div className="rounded-lg border border-success/30 bg-success/10 p-3 text-sm text-success">
              {importSummary}
            </div>
          ) : null}
          {warnings.length > 0 ? (
            <div className="rounded-lg border border-primary/30 bg-primary/10 p-3 text-sm text-primary">
              <p className="font-medium">Import notes</p>
              <ul className="mt-2 list-disc pl-5">
                {warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghostSecondary" onClick={handleClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};
